import Layout from "../components/Layout";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    FaCheck,
    FaCalendarAlt,
    FaChartLine,
    FaUsers,
    FaSync
} from "react-icons/fa";

const rawBackendUrl = process.env.REACT_APP_BACKEND_URL || "";
const backend_url = rawBackendUrl.startsWith("http://") || rawBackendUrl.startsWith("https://") ? rawBackendUrl : `https://${rawBackendUrl}`;
const API_URL = `${backend_url}/api`;
const getToken = () => localStorage.getItem("access_token");

function KpiCard({ label, value, color, delay, icon, onClick }) {
    const [show, setShow] = useState(false);
    const [hover, setHover] = useState(false);

    useEffect(() => {
        setTimeout(() => setShow(true), delay);
    }, [delay]);

    return (
        <div
            onClick={onClick}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                ...styles.kpiCard,
                borderLeft: `4px solid ${color}`,
                opacity: show ? 1 : 0,
                transform: show
                    ? hover
                        ? "translateY(-6px) scale(1.03)"
                        : "translateY(0)"
                    : "translateY(12px)",
                boxShadow: hover
                    ? "0 12px 30px rgba(94,23,235,0.25)"
                    : "0 4px 12px rgba(0,0,0,0.06)",
                cursor: onClick ? "pointer" : "default",
            }}
        >
            <div style={styles.kpiHeader}>
                {icon && <span style={{ marginRight: "8px", color }}>{icon}</span>}
                <span style={styles.kpiLabel}>{label}</span>
            </div>
            <strong style={{ ...styles.kpiValue, color }}>{value}</strong>
        </div>
    );
}

function TableBox({ title, rows, onViewAll, viewAllLink }) {
    const navigate = useNavigate();

    return (
        <div style={styles.box}>
            <div style={styles.boxHeader}>
                <h3 style={styles.boxTitle}>{title}</h3>
                {viewAllLink && (
                    <button onClick={() => navigate(viewAllLink)} style={styles.viewAllButton}>
                        Voir tout →
                    </button>
                )}
            </div>
            <table style={styles.table}>
                <tbody>
                    {rows && rows.length > 0 ? (
                        rows.map((row, i) => {
                            if (!row || !Array.isArray(row)) return null;
                            return (
                                <tr key={i} style={styles.tableRow}>
                                    {row.map((cell, j) => (
                                        <td key={j} style={cell?.style || {}}>
                                            {cell?.value || ''}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })
                    ) : (
                        <tr>
                            <td colSpan="3" style={styles.emptyCell}>
                                Aucune donnée disponible
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        totalReunions: 0,
        reunionsPrevues: 0,
        reunionsRealisees: 0,
        tauxRealisation: "0%",
        utilisateursTotal: 0
    });
    const [prochainesReunions, setProchainesReunions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [refreshing, setRefreshing] = useState(false);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchAllPages = async (url) => {
        let allItems = [];
        let nextUrl = url;
        const token = getToken();
        
        try {
            while (nextUrl) {
                console.log(`📄 Fetching: ${nextUrl}`);
                const response = await fetch(nextUrl, {
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
                
                if (!response.ok) {
                    console.error(`❌ Error ${response.status}: ${nextUrl}`);
                    break;
                }
                
                const data = await response.json();
                
                let items = [];
                if (data.results && Array.isArray(data.results)) {
                    items = data.results;
                    nextUrl = data.next || null;
                } else if (Array.isArray(data)) {
                    items = data;
                    nextUrl = null;
                } else if (data.data && Array.isArray(data.data)) {
                    items = data.data;
                    nextUrl = null;
                } else {
                    break;
                }
                
                allItems = [...allItems, ...items];
                console.log(`✅ Fetched ${items.length} items, total so far: ${allItems.length}`);
            }
        } catch (err) {
            console.error("❌ Error fetching paginated data:", err);
        }
        
        return allItems;
    };

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            setError("");
            const token = getToken();

            if (!token) {
                setError("Vous devez être connecté");
                setLoading(false);
                return;
            }

            console.log("🔐 Token présent, chargement des réunions...");

            const allReunions = await fetchAllPages(`${API_URL}/reunions/`);
            console.log("📊 Total réunions récupérées:", allReunions.length);
            
            // FIXED STATISTICS CALCULATION
            const totalReunions = allReunions.length;
            
            // Réunions prévues = status 'planifiee' AND validee = true
            const reunionsPlanifiees = allReunions.filter(r => r && r.statut === 'planifiee' && r.validee === true).length;
            
            // Réunions réalisées = status 'terminee' (regardless of validee)
            const reunionsTerminees = allReunions.filter(r => r && r.statut === 'terminee').length;
            
            // Calculate taux de réalisation based on completed vs planned
            let tauxRealisation = "0%";
            if (reunionsPlanifiees > 0) {
                const taux = (reunionsTerminees / reunionsPlanifiees) * 100;
                tauxRealisation = `${Math.round(taux)}%`;
            } else if (reunionsTerminees > 0) {
                tauxRealisation = "100%";
            }
            
            // Get upcoming meetings (next 5)
            const now = new Date();
            const upcomingReunions = allReunions
                .filter(r => r && r.statut === 'planifiee' && r.validee === true && r.date_heure && new Date(r.date_heure) > now)
                .sort((a, b) => new Date(a.date_heure) - new Date(b.date_heure))
                .slice(0, 5);
            
            const allUsers = await fetchAllPages(`${API_URL}/users/`);
            console.log("👥 Total utilisateurs:", allUsers.length);
            
            setStats({
                totalReunions: totalReunions,
                reunionsPrevues: reunionsPlanifiees,
                reunionsRealisees: reunionsTerminees,
                tauxRealisation: tauxRealisation,
                utilisateursTotal: allUsers.length
            });
            
            const prochainesReunionsRows = upcomingReunions.map(r => [
                { value: <span style={styles.clickable} onClick={() => navigate(`/reunions/${r.id}`)}>{r.titre || 'Sans titre'}</span> },
                { value: r.salle || 'Salle non définie' },
                { value: r.date_heure ? new Date(r.date_heure).toLocaleDateString('fr-FR') : 'Date non définie', style: styles.muted }
            ]);
            
            setProchainesReunions(prochainesReunionsRows);
            
        } catch (error) {
            console.error("❌ Erreur:", error);
            setError("Erreur de chargement des données: " + error.message);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleRefresh = () => {
        setRefreshing(true);
        fetchDashboardData();
    };

    if (loading) {
        return (
            <Layout>
                <div style={styles.loadingContainer}>
                    <div style={styles.spinner}></div>
                    <p>Chargement du dashboard...</p>
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <div style={styles.errorContainer}>
                    <p style={{ color: "#ef4444" }}>❌ {error}</p>
                    <button onClick={fetchDashboardData} style={styles.retryButton}>
                        <FaSync className="mr-2" size={14} />
                        Réessayer
                    </button>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div style={styles.page}>
                <div style={styles.header}>
                    <div>
                        <h1 style={styles.title}>Dashboard Administrateur</h1>
                        <p style={styles.subtitle}>Pilotage global du projet PolyMeet</p>
                    </div>
                    
                </div>

                <div style={styles.kpiRow}>
                    <KpiCard label="Total réunions" value={stats.totalReunions} color="#5e17eb" delay={100} icon={<FaCalendarAlt size={16} />} onClick={() => navigate('/admin-reunions')} />
                    <KpiCard label="Réunions prévues" value={stats.reunionsPrevues} color="#0ea5e9" delay={200} icon={<FaCalendarAlt size={16} />} onClick={() => navigate('/admin-reunions')} />
                    <KpiCard label="Réunions réalisées" value={stats.reunionsRealisees} color="#22c55e" delay={300} icon={<FaCheck size={16} />} onClick={() => navigate('/admin-reunions')} />
                    <KpiCard label="Taux de réalisation" value={stats.tauxRealisation} color="#f59e0b" delay={400} icon={<FaChartLine size={16} />} />
                    <KpiCard label="Total utilisateurs" value={stats.utilisateursTotal} color="#f59e0b" delay={500} icon={<FaUsers size={16} />} onClick={() => navigate('/listeUtilisateurs')} />
                </div>

                <div style={styles.tablesRow}>
                    <TableBox title="Prochaines réunions" rows={prochainesReunions} viewAllLink="/admin-reunions" />
                    <TableBox title="Statistiques globales" rows={[
                        [{ value: "Total réunions" }, { value: stats.totalReunions, style: { fontWeight: 500 } }],
                        [{ value: "Total utilisateurs" }, { value: stats.utilisateursTotal, style: { fontWeight: 500 } }],
                        [{ value: "Taux complétion" }, { value: stats.tauxRealisation, style: { color: "#0ea5e9", fontWeight: 500 } }]
                    ]} />
                </div>
            </div>
        </Layout>
    );
}

const styles = {
    page: { background: "#f7f6ff", padding: "22px", borderRadius: "16px" },
    header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "18px", flexWrap: "wrap", gap: "16px" },
    refreshButton: { display: "flex", alignItems: "center", gap: "8px", padding: "8px 16px", background: "white", border: "1px solid #e5e7eb", borderRadius: "8px", color: "#5e17eb", fontSize: "14px", fontWeight: "500", cursor: "pointer" },
    title: { fontSize: "26px", color: "#1f1f2e", fontWeight: "500", marginBottom: "4px" },
    subtitle: { color: "#666", marginBottom: "0px", fontSize: "14px" },
    loadingContainer: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "400px", gap: "16px" },
    spinner: { width: "40px", height: "40px", border: "3px solid #f3f3f3", borderTop: "3px solid #5e17eb", borderRadius: "50%", animation: "spin 1s linear infinite" },
    errorContainer: { textAlign: "center", padding: "40px", background: "white", borderRadius: "12px" },
    retryButton: { marginTop: "12px", padding: "8px 16px", background: "#5e17eb", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", margin: "0 auto" },
    kpiRow: { display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "14px", marginBottom: "22px" },
    kpiCard: { background: "white", padding: "12px 14px", borderRadius: "10px", transition: "all 0.3s ease" },
    kpiHeader: { display: "flex", alignItems: "center" },
    kpiLabel: { fontSize: "12px", color: "#555" },
    kpiValue: { fontSize: "20px", display: "block", marginTop: "4px" },
    tablesRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px" },
    box: { background: "white", borderRadius: "14px", padding: "16px", boxShadow: "0 4px 14px rgba(0,0,0,0.05)" },
    boxHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" },
    boxTitle: { fontSize: "15px", color: "#5e17eb", fontWeight: "600" },
    viewAllButton: { background: "transparent", border: "none", color: "#5e17eb", fontSize: "12px", cursor: "pointer", padding: "4px 8px", borderRadius: "4px" },
    table: { width: "100%", fontSize: "13px" },
    tableRow: { transition: "background 0.25s ease" },
    clickable: { cursor: "pointer", color: "#1f1f2e", textDecoration: "none" },
    muted: { color: "#888", textAlign: "right" },
    emptyCell: { textAlign: "center", color: "#9ca3af", padding: "20px 0" },
};

const styleSheet = document.createElement("style");
styleSheet.innerText = `@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } } .spin { animation: spin 1s linear infinite; }`;
document.head.appendChild(styleSheet);